/**
 * The three ways a receipt gets in, the staging list they all end at, and the
 * quota bar that says whether the next one will fit.
 *
 * It is a component of its own because there are two places a receipt is
 * added and they are the same act: the panel under an expense, which knows
 * what it is attaching to, and the Receipts tab, which does not yet — the
 * photograph is taken at the till and filed that evening. Everything between
 * picking the file and the `POST` is identical, and the parts that are easy to
 * get subtly wrong (releasing a `blob:` URL, emptying the input so the same
 * file can be picked twice, stopping a batch at the first failure with the
 * rest still staged) are exactly the parts a second copy would drift on.
 *
 * **The bar is drawn before the upload, not after it.** Picking a file does
 * not send it — it decodes it, redraws it at 2000px as WebP and makes a 400px
 * thumbnail, and *then* the bar moves, by the size of what would actually be
 * stored rather than the size of what came out of the camera. That is the
 * difference between "this 6 MB photo will not fit in your last 800 KB" (which
 * is false: it is 400 KB by the time it is sent) and the truth. It is also why
 * the row says how much a file shrank by: the number is otherwise surprising
 * enough to look like a bug.
 *
 * **Three ways in, one road out.** A file off the disk, a photo straight from
 * the phone's camera app, and the in-app scanner all end at the same staged
 * list and the same request. The middle one is
 * `<input type="file" accept="image/*" capture="environment">` — one attribute
 * — and it is the one that always works: full sensor quality, every browser
 * including the iOS ones, and no permission of ours to refuse. The scanner is
 * offered on top of it where `getUserMedia` exists, for the two things a live
 * preview buys that the input cannot: seeing the crop before the shutter, and
 * several pages without leaving the screen. It is loaded only when it is
 * opened, so nobody who never scans anything downloads a scanner.
 *
 * **Staging is a list, because a scan is often several pages.** The bar sums
 * the whole batch, so four pages are one decision about the quota rather than
 * four; the upload is one request per page, in order, and a failure halfway
 * leaves what has already been stored stored and the rest still staged.
 *
 * Nothing here is the enforcement. `server/receipts.ts` takes the same limits
 * again from the resolver on the request that stores the bytes, because a
 * client can be lied to and this one is only trying to save somebody a refusal
 * they could have been told about a second earlier.
 */

import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import type { Storage } from '../api'
import { scanningIsPossible } from '../lib/edge-finder'
import { describeQuota, formatBytes } from '../lib/format'
import { previewUrl, releasePreview } from '../lib/preview-url'
import { quotaFor } from '../lib/receipt-file'
import { UnsupportedReceipt, prepareReceipt, type PreparedReceipt } from '../lib/receipt-image'

/**
 * The scanner, and everything it drags in — the worker, the edge detection,
 * the perspective maths — as a chunk that is fetched when somebody presses
 * "Scan" and never otherwise. Exactly what `pdfThumbnail` does for pdf.js, and
 * for the same reason: the app's first load should pay for the app.
 */
const CameraCapture = lazy(() => import('./CameraCapture'))

/** What the picker will offer. The server's allow-list is the real one. */
const ACCEPT = 'image/*,application/pdf'

export function ReceiptUploader({
  send,
  storage,
  canUpload,
  archived,
  what,
}: {
  /**
   * One prepared page, stored. It resolves with whatever the API answered, so
   * the panel can say "that is already attached" when the server recognised
   * the file's digest, and it throws the named refusal when it is refused.
   */
  send: (prepared: PreparedReceipt) => Promise<{ duplicate?: true }>
  /** From `GET /api/me`, or `null` while it is still being fetched. */
  storage: Storage | null
  /** `can_upload_attachments`, as the resolver reported it. */
  canUpload: boolean
  archived: boolean
  /** What these are going onto, so every control can name it out loud. */
  what: string
}) {
  const picker = useRef<HTMLInputElement>(null)
  const camera = useRef<HTMLInputElement>(null)
  const [staged, setStaged] = useState<PreparedReceipt[]>([])
  const [preparing, setPreparing] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [sending, setSending] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [duplicate, setDuplicate] = useState(false)

  // The bar is drawn for what is *staged*, so it answers the question before
  // the upload rather than reporting on it afterwards — and for the whole
  // batch, because four pages are one decision about the quota.
  const adding = staged.reduce((total, page) => total + page.storedBytes, 0)
  const largest = staged.reduce((biggest, page) => Math.max(biggest, page.file.size), 0)
  const reading = storage ? quotaFor(storage, adding, largest) : null

  // Closing the panel with something staged would otherwise leave a full-size
  // image pinned by its `blob:` URL for the life of the tab. The ref is so the
  // cleanup sees the last list rather than the one it was set up with.
  const held = useRef<PreparedReceipt[]>([])
  held.current = staged
  useEffect(() => {
    return () => {
      for (const page of held.current) releasePreview(page)
    }
  }, [])

  async function pick(file: File | undefined) {
    if (!file) return
    setProblem(null)
    setPreparing(true)
    try {
      const prepared = await prepareReceipt(file)
      setStaged((current) => [...current, prepared])
    } catch (err) {
      setProblem(
        err instanceof UnsupportedReceipt
          ? err.message
          : `That file could not be prepared: ${(err as Error).message}`,
      )
    } finally {
      setPreparing(false)
      // Emptied so that picking the same file twice fires `change` twice —
      // otherwise re-taking a photo after dropping it does nothing at all.
      if (picker.current) picker.current.value = ''
      if (camera.current) camera.current.value = ''
    }
  }

  function drop(index: number) {
    setStaged((current) => {
      const going = current[index]
      if (going) releasePreview(going)
      return current.filter((_, i) => i !== index)
    })
  }

  function clear() {
    setStaged((current) => {
      for (const page of current) releasePreview(page)
      return []
    })
    setProblem(null)
  }

  /**
   * The batch, one request each, in order.
   *
   * Stops at the first failure and keeps what is left staged, because the
   * pages that did go up are stored and re-sending them would be a second
   * copy — the server would recognise the duplicate by its hash, but the
   * upload would still have been paid for. What the person is left with is
   * exactly the pages that are not attached yet.
   */
  async function submit() {
    setProblem(null)
    setDuplicate(false)
    setSending(true)
    const rest = [...staged]
    try {
      while (rest.length > 0) {
        const answer = await send(rest[0])
        if (answer?.duplicate) setDuplicate(true)
        releasePreview(rest[0])
        rest.shift()
        setStaged([...rest])
      }
    } catch (err) {
      setProblem((err as Error).message)
    } finally {
      setStaged([...rest])
      setSending(false)
    }
  }

  const busy = preparing || sending
  const blocked =
    !canUpload || archived || reading?.fits === false || reading?.fileFits === false

  return (
    <div className="uploader">
      {scanning && (
        <Suspense fallback={<p className="muted note">Getting the camera ready…</p>}>
          <CameraCapture
            onCancel={() => setScanning(false)}
            onUse={(pages) => {
              setScanning(false)
              setStaged((current) => [...current, ...pages])
            }}
            // The fallback path: no camera of ours, so the phone's own.
            onPickFile={(file) => {
              setScanning(false)
              void pick(file)
            }}
          />
        </Suspense>
      )}

      {/* What is about to go up, so the batch can be checked and thinned
          before the quota bar's answer is acted on. */}
      {staged.length > 0 && (
        <ul className="receipts__list receipts__list--staged">
          {staged.map((page, index) => (
            <li key={index} className="receipts__item">
              {page.thumbnail ? (
                <img
                  className="receipts__thumb"
                  src={previewUrl(page, page.thumbnail)}
                  alt={`Waiting to be attached: ${page.originalName}`}
                  width={120}
                  height={120}
                />
              ) : (
                <span className="receipts__thumb receipts__thumb--none" aria-hidden="true">
                  {page.kind === 'pdf' ? 'PDF' : '🧾'}
                </span>
              )}
              <span className="receipts__name">{page.originalName}</span>
              <span className="receipts__size">{describePreparation(page)}</span>
              <button
                type="button"
                className="button--quiet receipts__remove"
                disabled={sending}
                onClick={() => drop(index)}
              >
                Drop<span className="visually-hidden"> {page.originalName}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* The quota, stated in words as well as drawn as a bar — a bar on its
          own is a picture of a number nobody has been told. */}
      {reading && (
        <div className="quota">
          <div
            className={`quota__bar${reading.fits ? '' : ' quota__bar--over'}`}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(reading.fraction * 100)}
            aria-label="Storage used"
          >
            <span
              className="quota__used"
              style={{ width: `${Math.round((reading.usedBytes / (reading.limitBytes || 1)) * 100)}%` }}
            />
            {reading.addingBytes > 0 && (
              <span
                className="quota__adding"
                style={{
                  width: `${Math.min(
                    100,
                    Math.round((reading.addingBytes / (reading.limitBytes || 1)) * 100),
                  )}%`,
                }}
              />
            )}
          </div>
          <p className={`note ${reading.fits ? 'muted' : 'bad'}`}>{describeQuota(reading)}</p>
        </div>
      )}

      {!canUpload && (
        <p className="bad note">
          Attachments are switched off for this account. Get in touch if that looks wrong.
        </p>
      )}

      <p className="actions actions--tight">
        {/* One attribute — `capture` — and this is the phone's own camera at
            full sensor quality, on every browser, without asking us for
            anything. It stays whether or not the scanner below works. */}
        <label className="button--quiet receipts__pick">
          {preparing ? 'Preparing…' : 'Take a photo'}
          <span className="visually-hidden"> for {what}</span>
          <input
            ref={camera}
            className="visually-hidden"
            type="file"
            accept="image/*"
            capture="environment"
            disabled={busy || !canUpload || archived}
            onChange={(event) => void pick(event.target.files?.[0])}
          />
        </label>

        {scanningIsPossible() && (
          <button
            type="button"
            className="button--quiet"
            disabled={busy || !canUpload || archived || scanning}
            onClick={() => setScanning(true)}
          >
            Scan with the camera
          </button>
        )}

        <label className="button--quiet receipts__pick">
          Choose a file
          <span className="visually-hidden"> for {what}</span>
          <input
            ref={picker}
            className="visually-hidden"
            type="file"
            accept={ACCEPT}
            disabled={busy || !canUpload || archived}
            onChange={(event) => void pick(event.target.files?.[0])}
          />
        </label>
      </p>

      {staged.length > 0 && (
        <p className="actions actions--tight">
          <button type="button" disabled={sending || blocked} onClick={() => void submit()}>
            {sending
              ? 'Attaching…'
              : staged.length > 1
                ? `Attach ${staged.length} pages`
                : 'Attach'}
          </button>
          <button type="button" className="button--quiet" disabled={sending} onClick={clear}>
            Cancel
          </button>
        </p>
      )}

      {problem && <p className="bad note">{problem}</p>}
      {duplicate && (
        <p className="muted note">
          That is the same file as one already here, so nothing new was stored.
        </p>
      )}
    </div>
  )
}

/**
 * What the browser did to the file, in a sentence. The shrinking is worth
 * saying out loud: "6.2 MB → 412 KB" is the difference between somebody
 * believing the quota bar and thinking it is broken.
 *
 * A scanned page has no "before" — it never was a file — so it lands in the
 * middle branch and simply says how big it is.
 */
function describePreparation(prepared: PreparedReceipt): string {
  if (prepared.kind === 'pdf') {
    return `${formatBytes(prepared.storedBytes)}, uploaded as it is`
  }
  if (prepared.originalBytes <= prepared.storedBytes) {
    return `${formatBytes(prepared.storedBytes)}`
  }
  return `${formatBytes(prepared.originalBytes)} shrunk to ${formatBytes(prepared.storedBytes)}`
}
